import { memo } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ChevronRight, Wrench, LucideIcon, Loader2 } from "lucide-react";
import { useSluzby, Sluzba } from "@/hooks/useSluzby";

const getIconComponent = (iconName: string | null): LucideIcon => {
  if (!iconName) return Wrench;
  const IconComponent = (LucideIcons as unknown as Record<string, LucideIcon>)[iconName];
  return IconComponent || Wrench;
};

const ServiceCardCompact = memo(({ service }: { service: Sluzba }) => {
  const IconComponent = getIconComponent(service.icon);
  const linkPath = service.slug ? `/sluzby/${service.slug}` : "/sluzby";
  
  return (
    <Link to={linkPath} className="group block h-full">
      <article 
        className="relative h-full p-4 md:p-5 bg-card border border-border/60 rounded-xl 
          hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5
          transition-all duration-200 cursor-pointer"
        itemScope
        itemType="https://schema.org/Service"
      >
        {/* Icon */}
        <div className="w-11 h-11 md:w-12 md:h-12 mb-3 rounded-xl 
          bg-gradient-to-br from-primary/20 to-primary/5 
          flex items-center justify-center
          group-hover:scale-105 transition-transform duration-200">
          <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-primary" />
        </div>

        {/* Title */}
        <h3 
          className="font-semibold text-foreground text-sm md:text-base leading-tight mb-2 
            group-hover:text-primary transition-colors line-clamp-2"
          itemProp="name"
        >
          {service.title}
        </h3>

        {/* Arrow indicator */}
        <div className="flex items-center text-xs text-muted-foreground group-hover:text-primary transition-colors">
          <span className="hidden sm:inline">Více info</span>
          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
        </div>

        {/* Hover glow effect */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 to-transparent 
          opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        
        <meta itemProp="provider" content="ESKO Servis" />
        <meta itemProp="areaServed" content="Teplice, Ústecký kraj" />
      </article>
    </Link>
  );
});

ServiceCardCompact.displayName = "ServiceCardCompact";

// Static fallback services for SEO - shown while loading
const staticServices: Sluzba[] = [
  { id: "static-1", title: "Elektroinstalační práce", slug: "elektroinstalacni-prace", icon: "Zap", description: null, details: null, display_order: 1, is_active: true, created_at: "" },
  { id: "static-2", title: "Elektromontážní práce", slug: "elektromontazni-prace", icon: "Cable", description: null, details: null, display_order: 2, is_active: true, created_at: "" },
  { id: "static-3", title: "Revize elektroinstalací", slug: "revize-elektroinstalaci", icon: "FileCheck", description: null, details: null, display_order: 3, is_active: true, created_at: "" },
  { id: "static-4", title: "Fotovoltaické systémy", slug: "fotovoltaicke-systemy", icon: "Battery", description: null, details: null, display_order: 4, is_active: true, created_at: "" },
  { id: "static-5", title: "Demontážní práce", slug: "demontazni-prace", icon: "Wrench", description: null, details: null, display_order: 5, is_active: true, created_at: "" },
  { id: "static-6", title: "Zabezpečovací systémy", slug: "zabezpecovaci-systemy", icon: "Shield", description: null, details: null, display_order: 6, is_active: true, created_at: "" },
];

const Services = () => {
  const { sluzby, loading } = useSluzby();

  // Use static services while loading for immediate SEO content
  const servicesToRender = loading ? staticServices : sluzby.filter(s => s.is_active);

  if (servicesToRender.length === 0) {
    return null;
  }

  return (
    <div 
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4"
      itemScope
      itemType="https://schema.org/ItemList"
    >
      <meta itemProp="name" content="Elektrikářské služby ESKO Servis" />
      <meta itemProp="numberOfItems" content={String(servicesToRender.length)} />
      {servicesToRender.map((service, index) => (
        <div key={service.id} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
          <meta itemProp="position" content={String(index + 1)} />
          <ServiceCardCompact service={service} />
        </div>
      ))}
    </div>
  );
};

export default Services;
